/**
 * Draft invoice editor (#2150).
 *
 * GET invoice seeds the shared InvoiceLinesEditor; PATCH /invoices/:id saves
 * a full line replacement (group metadata included) with expectedVersion.
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import type { InvoiceDetail } from '@/hooks/useInvoices';
import { InvoiceLinesEditor } from './InvoiceLinesEditor';
import type { DraftInvoiceEditorModel } from './useDraftInvoiceEditor';

function formatPercent(percent: number): string {
  return (Math.round(percent * 100) / 100).toString().replace('.', ',');
}

function TotalsRow({
  label,
  value,
  bold = false,
  color,
}: {
  label: string;
  value: string;
  bold?: boolean;
  color?: string;
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" fontWeight={bold ? 700 : 400} {...(color ? { color } : {})}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 500} {...(color ? { color } : {})}>
        {value}
      </Typography>
    </Box>
  );
}

interface DraftInvoiceEditorProps {
  invoice: InvoiceDetail;
  model: DraftInvoiceEditorModel;
}

export function DraftInvoiceEditor({ invoice, model }: DraftInvoiceEditorProps) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const {
    lines,
    setLines,
    dueDate,
    setDueDate,
    paymentTermsDays,
    setPaymentTermsDays,
    paymentTermsOptions,
    customerReference,
    setCustomerReference,
    paymentReferenceNumber,
    setPaymentReferenceNumber,
    notes,
    setNotes,
    termsAndConditions,
    setTermsAndConditions,
    totals,
    save,
    saving,
    isDirty,
    staleConflict,
    reload,
    finalizeBlockedReason,
  } = model;

  const termsSelectValue = paymentTermsOptions.includes(paymentTermsDays)
    ? paymentTermsDays
    : (paymentTermsOptions[0] ?? '');

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid="draft-invoice-editor">
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {t('edit.title')}
      </Typography>

      {staleConflict ? (
        <Alert
          severity="error"
          data-testid="draft-invoice-stale-alert"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<ReplayIcon />}
              onClick={() => void reload()}
            >
              {t('edit.reload')}
            </Button>
          }
        >
          {t('error.staleVersion')}
        </Alert>
      ) : null}

      {finalizeBlockedReason && !staleConflict ? (
        <Alert
          severity="warning"
          data-testid="draft-invoice-finalize-blocked"
          sx={{ mb: 2 }}
        >
          {finalizeBlockedReason}
        </Alert>
      ) : null}

      <InvoiceLinesEditor
        lines={lines}
        onChange={setLines}
        fallbackTaxPercent={invoice.taxPercent}
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ ml: 'auto', maxWidth: 360 }} data-testid="draft-invoice-totals">
        <TotalsRow label={t('detail.subtotal')} value={formatPrice(totals.subtotalCents)} />
        {totals.taxBreakdown.filter((group) => group.taxPercent > 0).map((group) => (
          <TotalsRow
            key={group.taxPercent}
            label={t('detail.tax', { percent: formatPercent(group.taxPercent) })}
            value={formatPrice(group.taxCents)}
          />
        ))}
        {totals.discountCents > 0 && (
          <TotalsRow
            label={t('detail.discount')}
            value={`-${formatPrice(totals.discountCents)}`}
            color="error.main"
          />
        )}
        <Divider sx={{ my: 1 }} />
        <Box data-testid="draft-invoice-total">
          <TotalsRow
            label={t('edit.totalInclVat')}
            value={formatPrice(totals.totalCents)}
            bold
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('edit.serverConfirms')}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('edit.paymentDefaults')}
      </Typography>
      <Stack spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel id="draft-payment-term-label">{t('generate.paymentTerm')}</InputLabel>
          <Select
            labelId="draft-payment-term-label"
            label={t('generate.paymentTerm')}
            value={termsSelectValue}
            onChange={(event) => setPaymentTermsDays(Number(event.target.value))}
            inputProps={{ 'data-testid': 'draft-payment-terms-select' }}
          >
            {paymentTermsOptions.map((days) => (
              <MenuItem key={days} value={days}>
                {t('generate.paymentTermDays', { count: days })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          type="date"
          size="small"
          fullWidth
          label={t('generate.dueDate')}
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'aria-label': t('generate.dueDate') },
          }}
          required
        />
        <TextField
          size="small"
          fullWidth
          label={t('edit.customerReference')}
          value={customerReference}
          onChange={(event) => setCustomerReference(event.target.value)}
        />
        <TextField
          size="small"
          fullWidth
          label={t('edit.paymentReferenceNumber')}
          value={paymentReferenceNumber}
          onChange={(event) => setPaymentReferenceNumber(event.target.value)}
        />
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label={t('generate.notes')}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label={t('generate.terms')}
          value={termsAndConditions}
          onChange={(event) => setTermsAndConditions(event.target.value)}
        />
        <Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => void save()}
            disabled={!isDirty || saving}
            data-testid="draft-invoice-save"
          >
            {saving ? tc('saving') : tc('save')}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
